import { Breadcrumb } from '@bugsnag/js';

/** Requests waiting for their Bugsnag breadcrumb; old entries are dropped so aborted requests don't pile up. */
const MAX_PENDING_KEYS = 200;
const pending = new Map<string, string[]>();

/** Latest trace ID for `GET /api/user`. Kept apart from `pending` because breadcrumbs consume that map. */
let latestUserRequestTraceId: string | undefined;

function isUserRequest(method: string, url: string): boolean {
  if (method.toUpperCase() !== 'GET') {
    return false;
  }
  try {
    return new URL(url, 'http://localhost').pathname.replace(/\/+$/, '') === '/api/user';
  } catch {
    return false;
  }
}

const key = (method: string, url: string) => `${method.toUpperCase()} ${url}`;

/**
 * Records the Datadog trace ID `TraceIdInterceptor` put on a request, keyed by method and the URL
 * the browser requests (`urlWithParams`), so the matching Bugsnag request breadcrumb can carry it.
 * Identical requests in flight at once are matched first-in, first-out.
 */
export function recordRequestTraceId(method: string, url: string, traceId: string): void {
  if (isUserRequest(method, url)) {
    latestUserRequestTraceId = traceId;
  }
  const k = key(method, url);
  const ids = pending.get(k) ?? [];
  ids.push(traceId);
  pending.delete(k);
  pending.set(k, ids);
  if (pending.size > MAX_PENDING_KEYS) {
    pending.delete(pending.keys().next().value as string);
  }
}

/** The Datadog trace ID of the most recent `GET /api/user`, for pages that show a reference ID. */
export function getLatestUserRequestTraceId(): string | undefined {
  return latestUserRequestTraceId;
}

function takeTraceId(method: string, url: string): string | undefined {
  const k = key(method, url);
  const ids = pending.get(k);
  const traceId = ids?.shift();
  if (ids?.length === 0) {
    pending.delete(k);
  }
  return traceId;
}

/** Bugsnag `onBreadcrumb` callback: adds `datadogTraceId` to request breadcrumbs for traced requests. */
export function attachTraceIdToBreadcrumb(breadcrumb: Breadcrumb): void {
  if (breadcrumb.type !== 'request') {
    return;
  }
  const { method, url } = breadcrumb.metadata ?? {};
  if (typeof method !== 'string' || typeof url !== 'string') {
    return;
  }
  const traceId = takeTraceId(method, url);
  if (traceId) {
    breadcrumb.metadata.datadogTraceId = traceId;
  }
}

/** For specs. */
export function clearRequestTraceIds(): void {
  pending.clear();
  latestUserRequestTraceId = undefined;
}
